<template>
  <v-card>
    <v-card-title>
      <span class="headline">Browse Projects</span>
    </v-card-title>

    <v-card-text>
      <!-- Token -->
      <v-form>
        <v-text-field
          v-model="localToken"
          label="API Token"
          :type="showApiToken ? 'text' : 'password'"
          append-icon="mdi-eye"
          @click:append="showApiToken = !showApiToken"
          required
        />
        <v-row align="stretch">
          <v-col cols="auto">
            <v-checkbox v-model="localSaveToken" label="Save API Token" />
          </v-col>
          <v-col cols="auto">
            <v-btn
              color="black darken-1"
              text
              class="normal-case-btn"
              :disabled="!localToken"
              @click="fetchProjects"
            >
              <v-icon>mdi-folder-sync</v-icon>
              Fetch Projects
            </v-btn>
          </v-col>
        </v-row>
      </v-form>

      <!-- Projects -->
      <v-container v-if="!selectedProject && !selectedDocument">
        <v-text-field
          v-model="projectSearch"
          density="compact"
          label="Search projects"
          prepend-inner-icon="mdi-magnify"
          variant="solo-filled"
          flat
          hide-details
          single-line
          class="mb-2"
        />
        <v-data-table
          :items="browserProjects"
          :headers="projectHeaders"
          item-value="id"
          class="elevation-1"
          dense
          :search="projectSearch"
          :items-per-page="25"
        >
          <template #top>
            <v-toolbar flat>
              <v-toolbar-title>Projects</v-toolbar-title>
            </v-toolbar>
          </template>

          <template #item="{ item }">
            <tr @click="selectProject(item)" class="clickable-row">
              <td><v-icon>mdi-folder</v-icon> {{ item.name }}</td>
              <td>{{ item.owner }}</td>
              <td>{{ item.documents_count }}</td>
              <td>{{ formatDate(item.created_at) }}</td>
              <td>{{ formatDate(item.updated_at) }}</td>
            </tr>
          </template>
        </v-data-table>
      </v-container>

      <!-- Documents (multi-select here) -->
      <v-container v-if="selectedProject && !selectedDocument">
        <v-toolbar flat>
          <v-btn icon @click="resetProjectSelection">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <v-toolbar-title>Documents</v-toolbar-title>
          <v-spacer />
        </v-toolbar>
        <v-text-field
          v-model="documentSearch"
          density="compact"
          label="Search documents"
          prepend-inner-icon="mdi-magnify"
          variant="solo-filled"
          flat
          hide-details
          single-line
          class="mb-2"
        />
        <v-data-table
          :items="browserDocuments"
          :headers="documentHeaders"
          item-value="pk"
          class="elevation-1"
          dense
          :search="documentSearch"
          :items-per-page="25"
        >
          <!-- Header checkbox (Select All) -->
          <template #header.select>
            <v-checkbox
              :indeterminate="isSomeDocsSelected"
              :model-value="areAllDocsSelected"
              @click.stop="toggleDocsSelectAll"
              hide-details
              density="compact"
            />
          </template>

          <template #item="{ item }">
            <tr class="clickable-row" @click="selectDocument(item)">
              <!-- row checkbox -->
              <td @click.stop>
                <v-checkbox
                  v-model="selectedDocumentPks"
                  :value="item.pk"
                  hide-details
                  density="compact"
                />
              </td>

              <td><v-icon>mdi-file-document</v-icon> {{ item.name }}</td>
              <td>{{ item.parts_count }}</td>
              <td>{{ formatDate(item.created_at) }}</td>
              <td>{{ formatDate(item.updated_at) }}</td>
            </tr>
          </template>
        </v-data-table>
      </v-container>

      <!-- Pages (when a single document is opened) -->
      <v-container v-if="selectedDocument">
        <v-btn icon @click="resetDocumentSelection">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>

        <v-row class="select-all-row" justify="space-between">
          <v-col cols="auto">
            <strong>{{ selectedDocument?.name }}</strong>
          </v-col>

          <!-- keep multi-select for pages mode -->
          <v-col cols="auto" v-if="mode === 'pages'">
            <v-btn small color="primary" @click="toggleBrowserSelectAll">
              <v-icon left>mdi-check-all</v-icon> Select All
            </v-btn>
          </v-col>
        </v-row>

        <v-row>
          <v-col
            v-for="page in browserPages"
            :key="page.pk"
            cols="12"
            md="4"
            lg="3"
          >
            <v-card>
              <v-img :src="`${baseURL}${page.image.thumbnails.card}`" />
              <template v-if="mode === 'pages'">
                <v-checkbox
                  v-model="selectedBrowserPages"
                  :value="page.pk"
                  :label="`Page ${page.pk}`"
                />
              </template>
              <template v-else>
                <div class="pa-3 text-caption">Page #{{ page.order+1 }}</div>
              </template>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>

    <!-- Footer actions -->
    <v-card-actions>
      <v-spacer />
      <v-btn text color="red" @click="$emit('close')">Cancel</v-btn>

      <!-- pages mode -->
      <v-btn
        v-if="mode === 'pages'"
        text color="blue"
        :disabled="!selectedBrowserPages.length"
        @click="emitSelectPages"
      >
        Import Selected Pages
      </v-btn>

      <!-- documents mode: list view (multi) -->
      <v-btn
        v-if="mode === 'documents' && !selectedDocument"
        text color="blue"
        :disabled="!selectedDocumentPks.length"
        @click="emitSelectDocuments"
      >
        Import Selected Documents
      </v-btn>

      <!-- documents mode: inside one doc -->
      <v-btn
        v-if="mode === 'documents' && selectedDocument"
        text color="blue"
        @click="emitSelectDocument"
      >
        Import This Document
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import axios from "axios";

export default {
  name: "EscriptoriumBrowser",
  props: {
    mode: { type: String, default: "pages" }, // 'pages' | 'documents'
    baseURL: {
      type: String,
      default: () => process.env.VUE_APP_ESCRIPTORIUM_BASE_URL || "",
    },
    apiToken: { type: String, default: "" },
    saveToken: { type: Boolean, default: false },
  },
  data() {
    return {
      localToken: this.apiToken,
      localSaveToken: this.saveToken,
      showApiToken: false,

      // lists
      browserProjects: [],
      browserDocuments: [],
      browserPages: [],

      // selection
      selectedProject: null,
      selectedDocument: null,
      selectedBrowserPages: [],
      selectedDocumentPks: [],

      // search
      projectSearch: '',
      documentSearch: '',

      projectHeaders: [
        { title: "Project Name", key: "name" },
        { title: "Owner", key: "owner" },
        { title: "Documents Count", key: "documents_count" },
        { title: "Created At", key: "created_at" },
        { title: "Updated At", key: "updated_at" },
      ],
      // add a "select" checkbox column to documents table
      documentHeaders: [
        { title: "", key: "select", sortable: false },
        { title: "Document Name", key: "name" },
        { title: "Pages Count", key: "parts_count" },
        { title: "Created At", key: "created_at" },
        { title: "Updated At", key: "updated_at" },
      ],
    };
  },
  computed: {
    areAllDocsSelected() {
      return (
        this.browserDocuments.length > 0 &&
        this.selectedDocumentPks.length === this.browserDocuments.length
      );
    },
    isSomeDocsSelected() {
      return (
        this.selectedDocumentPks.length > 0 &&
        this.selectedDocumentPks.length < this.browserDocuments.length
      );
    },
  },
  watch: {
    localSaveToken(newVal) {
      if (newVal && this.localToken) {
        localStorage.setItem("prefs.escriptoriumToken", this.localToken);
      }
      this.$emit("update:saveToken", newVal);
    },
    localToken(newVal) {
      this.$emit("update:apiToken", newVal);
    },
  },
  mounted() {
    if (!this.localToken) {
      const cached = localStorage.getItem("prefs.escriptoriumToken");
      if (cached) {
        this.localToken = cached;
        this.localSaveToken = true;
      }
    }
  },
  methods: {
    formatDate(d) {
      if (!d) return "";
      const date = new Date(d);
      return new Intl.DateTimeFormat("en-US", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(date);
    },

    async fetchProjects() {
      this.resetAll();
      if (!this.localToken) return;
      const headers = { Authorization: `Token ${this.localToken}` };
      let url = `${this.baseURL}/api/projects/`;
      const all = [];
      while (url) {
        const { data } = await axios.get(url, { headers });
        all.push(...(data.results || []));
        url = data.next ? this.resolveNextUrl(data.next) : null;
      }
      this.browserProjects = all;
      if (this.localSaveToken) localStorage.setItem("prefs.escriptoriumToken", this.localToken);
    },

    async fetchDocuments(projectId) {
      const headers = { Authorization: `Token ${this.localToken}` };
      let url = `${this.baseURL}/api/documents/?project=${projectId}`;
      const all = [];
      while (url) {
        const { data } = await axios.get(url, { headers });
        all.push(...(data.results || []));
        url = data.next ? this.resolveNextUrl(data.next) : null;
      }
      this.browserDocuments = all;
      this.selectedDocumentPks = this.selectedDocumentPks.filter(pk =>
        (this.browserDocuments || []).some(d => d.pk === pk)
      );
    },
      resolveNextUrl(next) {
    // make relative `next` absolute against baseURL
    try { return new URL(next, this.baseURL).toString(); }
    catch (_) { return next; }
  },
  async fetchPages(documentPk) {
    const headers = { Authorization: `Token ${this.localToken}` };
    let url = `${this.baseURL}/api/documents/${documentPk}/parts/?ordering=order&paginate_by=50`;
    const all = [];

    while (url) {
      const { data } = await axios.get(url, { headers });
      // accumulate current page
      all.push(...(data.results || []));
      // follow pagination until next is null
      url = data.next ? this.resolveNextUrl(data.next) : null;
      // (optional) you can read data.count if you need the total
      // const total = data.count;
    }

    this.browserPages = all;
  },

    selectProject(project) {
      this.selectedProject = project;
      this.fetchDocuments(project.id);
    },
    resetProjectSelection() {
      this.selectedProject = null;
      this.browserDocuments = [];
      this.selectedDocumentPks = [];
    },

    selectDocument(doc) {
      this.selectedDocument = doc;
      this.fetchPages(doc.pk);
    },
    resetDocumentSelection() {
      this.selectedDocument = null;
      this.browserPages = [];
    },

    // pages select-all (existing)
    toggleBrowserSelectAll() {
      if (!this.browserPages.length) return;
      if (this.selectedBrowserPages.length === this.browserPages.length) {
        this.selectedBrowserPages = [];
      } else {
        this.selectedBrowserPages = this.browserPages.map((p) => p.pk);
      }
    },

    // documents select-all (new)
    toggleDocsSelectAll() {
      if (!this.browserDocuments.length) return;
      if (this.areAllDocsSelected) {
        this.selectedDocumentPks = [];
      } else {
        this.selectedDocumentPks = this.browserDocuments.map((d) => d.pk);
      }
    },

    emitSelectPages() {
      if (!this.selectedDocument || !this.selectedBrowserPages.length) return;
      this.$emit("select-pages", {
        baseURL: this.baseURL,
        token: this.localToken,
        document: this.selectedDocument,
        pagePks: [...this.selectedBrowserPages],
      });
      this.$emit("close");
    },

    emitSelectDocument() {
      if (!this.selectedDocument) return;
      this.$emit("select-document", {
        baseURL: this.baseURL,
        token: this.localToken,
        document: this.selectedDocument,
      });
      this.$emit("close");
    },

    emitSelectDocuments() {
      if (!this.selectedDocumentPks.length) return;
      const selected = this.browserDocuments.filter(d =>
        this.selectedDocumentPks.includes(d.pk)
      );
      this.$emit("select-documents", {
        baseURL: this.baseURL,
        token: this.localToken,
        documents: selected,
      });
      this.$emit("close");
    },

    resetAll() {
      this.selectedProject = null;
      this.selectedDocument = null;
      this.browserProjects = [];
      this.browserDocuments = [];
      this.browserPages = [];
      this.selectedBrowserPages = [];
      this.selectedDocumentPks = [];
      this.projectSearch = '';
      this.documentSearch = '';
    },
  },
};
</script>

<style scoped>
.clickable-row { cursor: pointer; transition: background-color .2s; }
.clickable-row:hover { background-color: rgba(var(--v-border-color), 0.08); }
.normal-case-btn { text-transform: none !important; }
</style>
